/* eslint-disable max-lines-per-function */
import {
  GetBenachrichtigungenQuery,
  GetBenachrichtigungenQueryVariables,
  Navigation_Abwesenheitsgrund,
  Navigation_Markierung,
} from '@/gql/graphql';
import { useSuspenseQuery } from '@apollo/client';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/browser';
import {
  Abwesenheitsgrund,
  Arbeitsbereich,
  Benachrichtigungen,
  Markierung,
  Mitarbeiter,
} from '@/domain/navigation/benachrichtigungen/types/Benachrichtungen';
import { GET_BENACHRICHTIGUNGEN } from '@/domain/navigation/benachrichtigungen/graphql/queries';
import { Abwesenheitsantraege } from '@/domain/navigation/benachrichtigungen/types/AbwesenheitsAntraege';
import { Fristen } from '@/domain/navigation/benachrichtigungen/types/Fristen';
import { FehlendeArbeitszeiten } from '@/domain/navigation/benachrichtigungen/types/FehlendeArbeitszeiten';
import { Krankmeldungen } from '@/domain/navigation/benachrichtigungen/types/Krankmeldungen';
import { Dokumente } from '@/domain/navigation/benachrichtigungen/types/Dokumente';
import { Personalfragebogen } from '@/domain/navigation/benachrichtigungen/types/Personalfragebogen';
import {
  Schicht,
  SchichttauschAntrag,
} from '@/domain/navigation/benachrichtigungen/types/SchichttauschAntrag';

const navigationToDomainAbwesenheitsgrundMap = {
  [Navigation_Abwesenheitsgrund.Arbeitsunfall]: Abwesenheitsgrund.ARBEITSUNFALL,
  [Navigation_Abwesenheitsgrund.Arztbesuch]: Abwesenheitsgrund.ARZTBESUCH,
  [Navigation_Abwesenheitsgrund.Ausgleichsruhezeitentag]:
    Abwesenheitsgrund.AUSGLEICHSRUHEZEITENTAG,
  [Navigation_Abwesenheitsgrund.Beschaeftigungsverbot]:
    Abwesenheitsgrund.BESCHAEFTIGUNGSVERBOT,
  [Navigation_Abwesenheitsgrund.Bezahlterarbeitsfreiertag]:
    Abwesenheitsgrund.BEZAHLTERARBEITSFREIERTAG,
  [Navigation_Abwesenheitsgrund.Bildung]: Abwesenheitsgrund.BILDUNG,
  [Navigation_Abwesenheitsgrund.Elternzeit]: Abwesenheitsgrund.ELTERNZEIT,
  [Navigation_Abwesenheitsgrund.Eurente]: Abwesenheitsgrund.EURENTE,
  [Navigation_Abwesenheitsgrund.Fehltunentschuldigt]:
    Abwesenheitsgrund.FEHLTUNENTSCHULDIGT,
  [Navigation_Abwesenheitsgrund.Feiertag]: Abwesenheitsgrund.FEIERTAG,
  [Navigation_Abwesenheitsgrund.Feiertagsausgleich]:
    Abwesenheitsgrund.FEIERTAGSAUSGLEICH,
  [Navigation_Abwesenheitsgrund.Feiertagsausgleichdynamisch]:
    Abwesenheitsgrund.FEIERTAGSAUSGLEICHDYNAMISCH,
  [Navigation_Abwesenheitsgrund.Ferientag]: Abwesenheitsgrund.FERIENTAG,
  [Navigation_Abwesenheitsgrund.Frei]: Abwesenheitsgrund.FREI,
  [Navigation_Abwesenheitsgrund.Freistellung]: Abwesenheitsgrund.FREISTELLUNG,
  [Navigation_Abwesenheitsgrund.Guttagminus]: Abwesenheitsgrund.GUTTAGMINUS,
  [Navigation_Abwesenheitsgrund.Guttagplus]: Abwesenheitsgrund.GUTTAGPLUS,
  [Navigation_Abwesenheitsgrund.Kompensationstag]:
    Abwesenheitsgrund.KOMPENSATIONSTAG,
  [Navigation_Abwesenheitsgrund.Krank]: Abwesenheitsgrund.KRANK,
  [Navigation_Abwesenheitsgrund.Krankengeld]: Abwesenheitsgrund.KRANKENGELD,
  [Navigation_Abwesenheitsgrund.Krankeskind]: Abwesenheitsgrund.KRANKESKIND,
  [Navigation_Abwesenheitsgrund.Krankimfrei]: Abwesenheitsgrund.KRANKIMFREI,
  [Navigation_Abwesenheitsgrund.Krankinkurzarbeit]:
    Abwesenheitsgrund.KRANKINKURZARBEIT,
  [Navigation_Abwesenheitsgrund.Krankohnelohnfortzahlung]:
    Abwesenheitsgrund.KRANKOHNELOHNFORTZAHLUNG,
  [Navigation_Abwesenheitsgrund.Kur]: Abwesenheitsgrund.KUR,
  [Navigation_Abwesenheitsgrund.Kurzarbeit]: Abwesenheitsgrund.KURZARBEIT,
  [Navigation_Abwesenheitsgrund.Lehrgang]: Abwesenheitsgrund.LEHRGANG,
  [Navigation_Abwesenheitsgrund.Militaerzivildienst]:
    Abwesenheitsgrund.MILITAERZIVILDIENST,
  [Navigation_Abwesenheitsgrund.Mutterschaft]: Abwesenheitsgrund.MUTTERSCHAFT,
  [Navigation_Abwesenheitsgrund.Mutterschutz]: Abwesenheitsgrund.MUTTERSCHUTZ,
  [Navigation_Abwesenheitsgrund.Quarantaene]: Abwesenheitsgrund.QUARANTAENE,
  [Navigation_Abwesenheitsgrund.Quarantaeneohnelfz]:
    Abwesenheitsgrund.QUARANTAENEOHNELFZ,
  [Navigation_Abwesenheitsgrund.Ruhetag]: Abwesenheitsgrund.RUHETAG,
  [Navigation_Abwesenheitsgrund.Schule]: Abwesenheitsgrund.SCHULE,
  [Navigation_Abwesenheitsgrund.Sonderurlaub]: Abwesenheitsgrund.SONDERURLAUB,
  [Navigation_Abwesenheitsgrund.Ueberstundenabbau]:
    Abwesenheitsgrund.UEBERSTUNDENABBAU,
  [Navigation_Abwesenheitsgrund.Unbezahlterferientag]:
    Abwesenheitsgrund.UNBEZAHLTERFERIENTAG,
  [Navigation_Abwesenheitsgrund.Unbezahlterurlaub]:
    Abwesenheitsgrund.UNBEZAHLTERURLAUB,
  [Navigation_Abwesenheitsgrund.Unfall]: Abwesenheitsgrund.UNFALL,
  [Navigation_Abwesenheitsgrund.Urlaub]: Abwesenheitsgrund.URLAUB,
  [Navigation_Abwesenheitsgrund.Vaterschaft]: Abwesenheitsgrund.VATERSCHAFT,
  [Navigation_Abwesenheitsgrund.Wiedereingliederung]:
    Abwesenheitsgrund.WIEDEREINGLIEDERUNG,
  [Navigation_Abwesenheitsgrund.Wunschfrei]: Abwesenheitsgrund.WUNSCHFREI,
} as const satisfies Record<Navigation_Abwesenheitsgrund, Abwesenheitsgrund>;

const navigationToDomainMarkierungMap = {
  [Navigation_Markierung.Black]: Markierung.BLACK,
  [Navigation_Markierung.Blue]: Markierung.BLUE,
  [Navigation_Markierung.Brown]: Markierung.BROWN,
  [Navigation_Markierung.Cyan]: Markierung.CYAN,
  [Navigation_Markierung.Darkblue]: Markierung.DARKBLUE,
  [Navigation_Markierung.Darkgreen]: Markierung.DARKGREEN,
  [Navigation_Markierung.Darkred]: Markierung.DARKRED,
  [Navigation_Markierung.Gold]: Markierung.GOLD,
  [Navigation_Markierung.Gray]: Markierung.GRAY,
  [Navigation_Markierung.Green]: Markierung.GREEN,
  [Navigation_Markierung.Lightblue]: Markierung.LIGHTBLUE,
  [Navigation_Markierung.Lightgray]: Markierung.LIGHTGRAY,
  [Navigation_Markierung.Lightgreen]: Markierung.LIGHTGREEN,
  [Navigation_Markierung.Lila]: Markierung.LILA,
  [Navigation_Markierung.Magenta]: Markierung.MAGENTA,
  [Navigation_Markierung.Orange]: Markierung.ORANGE,
  [Navigation_Markierung.Pink]: Markierung.PINK,
  [Navigation_Markierung.Red]: Markierung.RED,
  [Navigation_Markierung.Rose]: Markierung.ROSE,
  [Navigation_Markierung.Violett]: Markierung.VIOLETT,
  [Navigation_Markierung.Yellow]: Markierung.YELLOW,
} as const satisfies Record<Navigation_Markierung, Markierung>;

const mapAbwesenheitsgrund = (
  grund: Navigation_Abwesenheitsgrund,
): Abwesenheitsgrund => navigationToDomainAbwesenheitsgrundMap[grund];

const mapMarkierung = (markierung: Navigation_Markierung): Markierung =>
  navigationToDomainMarkierungMap[markierung];

export function useBenachrichtigungen(standortId: string): Benachrichtigungen {
  const { data, error } = useSuspenseQuery<
    GetBenachrichtigungenQuery,
    GetBenachrichtigungenQueryVariables
  >(GET_BENACHRICHTIGUNGEN, {
    variables: { standortId },
  });

  if (error) {
    Sentry.captureException(error);
    return new Benachrichtigungen({
      fehlendeArbeitszeiten: [],
      abwesenheitsantraege: [],
      fristen: [],
      krankmeldungen: [],
      dokumente: [],
      personalfragebogen: [],
      schichttauschAntraege: [],
    });
  }

  const fehlendeArbeitszeiten =
    data.navigation_benachrichtigungen2.fehlendeArbeitszeit.map(
      (b) =>
        new FehlendeArbeitszeiten({
          id: b.id,
          datum: dayjs(b.datum),
          status: b.status,
          mitarbeiter: new Mitarbeiter({
            vorname: b.mitarbeiter.vorname,
            nachname: b.mitarbeiter.nachname,
            id: b.mitarbeiter.id,
            bildUrl: b.mitarbeiter.bildUrl,
            arbeitnehmertyp: b.mitarbeiter.arbeitnehmertyp,
          }),
          arbeitsbereich: new Arbeitsbereich({
            name: b.arbeitsbereich.name,
            markierung: b.arbeitsbereich.markierung,
            id: b.arbeitsbereich.id,
          }),
        }),
    );

  const abwesenheitsantraege =
    data.navigation_benachrichtigungen2.abwesenheitsantraege.map(
      (antrag) =>
        new Abwesenheitsantraege({
          id: antrag.id,
          von: dayjs(antrag.von),
          bis: dayjs(antrag.bis),
          grund: mapAbwesenheitsgrund(antrag.grund),
          markierung: mapMarkierung(antrag.markierung),
          mitarbeiter: new Mitarbeiter({
            id: antrag.mitarbeiter.id,
            vorname: antrag.mitarbeiter.vorname,
            nachname: antrag.mitarbeiter.nachname,
            bildUrl: antrag.mitarbeiter.bildUrl,
            arbeitnehmertyp: antrag.mitarbeiter.arbeitnehmertyp,
          }),
        }),
    );

  const fristen = data.navigation_benachrichtigungen2.fristen.map(
    (b) =>
      new Fristen({
        id: b.id,
        ablaufZeitpunkt: dayjs(b.ablaufZeitpunkt),
        grund: b.grund,
        gelesen: b.gelesen,
        mitarbeiter: new Mitarbeiter({
          vorname: b.mitarbeiter.vorname,
          nachname: b.mitarbeiter.nachname,
          id: b.mitarbeiter.id,
          bildUrl: b.mitarbeiter.bildUrl,
          arbeitnehmertyp: b.mitarbeiter.arbeitnehmertyp,
        }),
      }),
  );

  const krankmeldungen = data.navigation_benachrichtigungen2.krankmeldungen.map(
    (krankmeldung) =>
      new Krankmeldungen({
        istKindkrank: krankmeldung.istKindkrank,
        mitarbeiter: new Mitarbeiter({
          vorname: krankmeldung.mitarbeiter.vorname,
          nachname: krankmeldung.mitarbeiter.nachname,
          bildUrl: krankmeldung.mitarbeiter.bildUrl,
          id: krankmeldung.mitarbeiter.id,
          istExtern: krankmeldung.mitarbeiter.istExtern,
          arbeitnehmertyp: krankmeldung.mitarbeiter.arbeitnehmertyp,
        }),
        voraussichtlichVon: dayjs(krankmeldung.voraussichtlichVon),
        voraussichtlichBis: dayjs(krankmeldung.voraussichtlichBis),
      }),
  );

  const dokumente = data.navigation_benachrichtigungen2.dokumente.map(
    (dokument) =>
      new Dokumente({
        mitarbeiter: new Mitarbeiter({
          vorname: dokument.mitarbeiter.vorname,
          nachname: dokument.mitarbeiter.nachname,
          bildUrl: dokument.mitarbeiter.bildUrl,
          id: dokument.mitarbeiter.id,
          arbeitnehmertyp: dokument.mitarbeiter.arbeitnehmertyp,
        }),
        erstellungsDatum: dayjs(dokument.erstellungsDatum),
        id: dokument.id,
        name: dokument.name,
      }),
  );

  const personalfragebogen =
    data.navigation_benachrichtigungen2.mitarbeiterantraege.map(
      (mitarbeiterantrag) =>
        new Personalfragebogen({
          id: mitarbeiterantrag.id,
          erstellungsDatum: dayjs(mitarbeiterantrag.erstellungsDatum),
          mitarbeiter: new Mitarbeiter({
            vorname: mitarbeiterantrag.mitarbeiter.vorname,
            nachname: mitarbeiterantrag.mitarbeiter.nachname,
            id: mitarbeiterantrag.mitarbeiter.id,
            bildUrl: mitarbeiterantrag.mitarbeiter.bildUrl,
            arbeitnehmertyp: mitarbeiterantrag.mitarbeiter.arbeitnehmertyp,
          }),
        }),
    );

  const schichttauschantraege =
    data.navigation_benachrichtigungen2.schichttauschantraege.map(
      (schichttauschAntrag) =>
        new SchichttauschAntrag({
          id: schichttauschAntrag.id,
          bemerkungAntragsteller: schichttauschAntrag.bemerkungAntragsteller,
          erstellungsDatum: schichttauschAntrag.erstellungsDatum,
          mitarbeiter: new Mitarbeiter({
            vorname: schichttauschAntrag.mitarbeiter.vorname,
            nachname: schichttauschAntrag.mitarbeiter.nachname,
            id: schichttauschAntrag.mitarbeiter.id,
            bildUrl: schichttauschAntrag.mitarbeiter.bildUrl,
            arbeitsbereich: new Arbeitsbereich({
              id: schichttauschAntrag.mitarbeiter.arbeitsbereich.id,
              name: schichttauschAntrag.mitarbeiter.arbeitsbereich.name,
              markierung:
                schichttauschAntrag.mitarbeiter.arbeitsbereich.markierung,
            }),
            arbeitnehmertyp: schichttauschAntrag.mitarbeiter.arbeitnehmertyp,
          }),
          schicht: new Schicht({
            arbeitsbereich: new Arbeitsbereich({
              id: schichttauschAntrag.schicht.arbeitsbereich.id,
              name: schichttauschAntrag.schicht.arbeitsbereich.name,
              markierung: schichttauschAntrag.schicht.arbeitsbereich.markierung,
            }),
            beginn: dayjs(schichttauschAntrag.schicht.beginn),
            ende: dayjs(schichttauschAntrag.schicht.ende),
          }),
        }),
    );

  return new Benachrichtigungen({
    fehlendeArbeitszeiten: fehlendeArbeitszeiten,
    abwesenheitsantraege: abwesenheitsantraege,
    fristen: fristen,
    krankmeldungen: krankmeldungen,
    dokumente: dokumente,
    personalfragebogen,
    schichttauschAntraege: schichttauschantraege,
  });
}
